import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2, ShieldOff, ShieldCheck, Clock, X, History, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * OrderControlPanel
 * -----------------
 * Admin-only UI to globally / on-schedule / per-franchise pause new
 * store-portal orders. Does NOT affect ERP order creation (per Feb 2026
 * spec — ERP always remains open).
 *
 * Layout: master toggle + message + scheduled window + per-franchise
 * block list + last 50 audit rows on the side.
 */
const OrderControlPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [newFranchiseId, setNewFranchiseId] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ctrlResp, frResp] = await Promise.all([
        axios.get(`${API}/admin/order-control`, { headers: authHeaders() }),
        axios.get(`${API}/onboarding`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setSettings(ctrlResp.data.settings);
      setAudit(ctrlResp.data.audit || []);
      setFranchises(frResp.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load order control settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (patch, optimistic) => {
    setSaving(true);
    try {
      if (optimistic) setSettings((prev) => ({ ...prev, ...optimistic }));
      const r = await axios.put(`${API}/admin/order-control`, patch, { headers: authHeaders() });
      setSettings(r.data.settings);
      // Reload audit so the new entry shows up.
      const ctrlResp = await axios.get(`${API}/admin/order-control`, { headers: authHeaders() });
      setAudit(ctrlResp.data.audit || []);
      toast.success('Order control updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      load(); // revert
    } finally {
      setSaving(false);
    }
  }, [load]);

  const onToggle = (next) => save({ acceptance_enabled: next }, { acceptance_enabled: next });

  const onMessageBlur = (val) => {
    if (val !== settings.block_message) save({ block_message: val });
  };

  const onScheduleChange = (field, val) => {
    // <input type="datetime-local"> gives `YYYY-MM-DDTHH:mm` — convert to
    // ISO (UTC) before persisting.
    const iso = val ? new Date(val).toISOString() : '';
    save({ [field]: iso });
  };

  const addFranchise = () => {
    const v = newFranchiseId.trim();
    if (!v) return;
    const next = Array.from(new Set([...(settings.blocked_franchise_ids || []), v]));
    save({ blocked_franchise_ids: next });
    setNewFranchiseId('');
  };

  const removeFranchise = (fid) => {
    const next = (settings.blocked_franchise_ids || []).filter((f) => f !== fid);
    save({ blocked_franchise_ids: next });
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="animate-spin text-slate-400" />
      </div>
    );
  }

  const isAccepting = settings.acceptance_enabled;
  const isoToInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    // Adjust to local for the datetime-local input.
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <div className="space-y-6" data-testid="order-control-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Order Control</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pause or release new store-portal orders. ERP order creation is always open and never affected.
          </p>
        </div>
        <div
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm shadow-sm ${
            isAccepting ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                         : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          data-testid="order-control-banner"
        >
          {isAccepting ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}
          {isAccepting ? 'STORES — ORDERS OPEN' : 'STORES — ORDERS BLOCKED'}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Master toggle */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Order Acceptance</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Master switch. When OFF, every store-portal order is rejected with HTTP 423 and the message below.
                  </p>
                </div>
                <Switch
                  checked={isAccepting}
                  onCheckedChange={onToggle}
                  disabled={saving}
                  data-testid="order-acceptance-toggle"
                />
              </div>
              <div className="mt-5">
                <label className="block text-xs uppercase tracking-wider text-slate-600 font-semibold mb-2">
                  Custom Block Message
                </label>
                <Textarea
                  defaultValue={settings.block_message}
                  onBlur={(e) => onMessageBlur(e.target.value)}
                  rows={3}
                  placeholder="e.g. Warehouse stock update in progress. Orders will resume at 5 PM."
                  data-testid="block-message-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Shown to store users as a popup when they try to submit an order. Save by clicking outside the box.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Duplicate Order Warning Period */}
          <DuplicateWarningCard />

          {/* Cart Retention Period */}
          <CartRetentionCard />

          {/* Invoice → Order one-time backfill */}
          <InvoiceOrderSyncBackfillCard />

          {/* Schedule */}
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-slate-500" />
                <h3 className="font-semibold text-lg text-slate-900">Scheduled Block Window (optional)</h3>
              </div>
              <p className="text-sm text-slate-500">
                Auto-block + auto-release on schedule. Useful for nightly stock counts or planned maintenance.
                Leave both blank to disable.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 font-semibold mb-1">
                    Block From
                  </label>
                  <Input
                    type="datetime-local"
                    value={isoToInputValue(settings.schedule_block_from)}
                    onChange={(e) => onScheduleChange('schedule_block_from', e.target.value)}
                    data-testid="schedule-from"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 font-semibold mb-1">
                    Block To
                  </label>
                  <Input
                    type="datetime-local"
                    value={isoToInputValue(settings.schedule_block_to)}
                    onChange={(e) => onScheduleChange('schedule_block_to', e.target.value)}
                    data-testid="schedule-to"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Per-franchise blocklist */}
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg text-slate-900">Block Specific Franchise(s)</h3>
              <p className="text-sm text-slate-500">
                Override the global toggle for selected franchises. Any franchise in this list is blocked even when orders are otherwise open.
              </p>
              <div className="flex gap-2">
                <select
                  className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={newFranchiseId}
                  onChange={(e) => setNewFranchiseId(e.target.value)}
                  data-testid="franchise-block-select"
                >
                  <option value="">— Select franchise to block —</option>
                  {franchises
                    .filter((f) => !(settings.blocked_franchise_ids || []).includes(f.franchise_id))
                    .map((f) => (
                      <option key={f.id} value={f.franchise_id}>
                        {f.franchise_id} — {f.name}
                      </option>
                    ))}
                </select>
                <Button onClick={addFranchise} disabled={!newFranchiseId || saving} data-testid="franchise-block-add">
                  <Plus size={16} className="mr-1" /> Block
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {(settings.blocked_franchise_ids || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No franchise-specific blocks active.</p>
                ) : (
                  (settings.blocked_franchise_ids || []).map((fid) => {
                    const f = franchises.find((x) => x.franchise_id === fid);
                    return (
                      <span
                        key={fid}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-800 text-xs font-medium"
                        data-testid={`blocked-franchise-${fid}`}
                      >
                        {fid}{f ? ` — ${f.name}` : ''}
                        <button onClick={() => removeFranchise(fid)} className="text-red-500 hover:text-red-700">
                          <X size={14} />
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit log */}
        <div className="lg:col-span-1">
          <Card className="border-slate-200 sticky top-4">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <History size={18} className="text-slate-500" />
                <h3 className="font-semibold text-lg text-slate-900">Audit Log</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">Most recent 50 changes</p>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="order-control-audit">
                {audit.length === 0 && (
                  <p className="text-sm text-slate-400 italic">No changes yet.</p>
                )}
                {audit.map((row) => (
                  <div key={row.id} className="border-l-2 border-slate-200 pl-3 pb-2">
                    <div className="text-xs text-slate-500">
                      {new Date(row.changed_at).toLocaleString()} · {row.changed_by_name || 'system'}
                    </div>
                    <div className="text-sm mt-1 space-y-0.5">
                      {Object.entries(row.diff || {}).map(([k, v]) => (
                        <div key={k} className="text-xs">
                          <span className="font-mono text-slate-700">{k}</span>:{' '}
                          <span className="text-slate-400">{JSON.stringify(v.from)}</span>
                          {' → '}
                          <span className="font-semibold text-slate-900">{JSON.stringify(v.to)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Duplicate Order Warning Period — small inline config that controls how
// far back the duplicate-pending detector looks when a franchise tries to
// re-order a SKU already in pending procurement. Warning-only; never blocks.
// ────────────────────────────────────────────────────────────────────
function DuplicateWarningCard() {
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);
  const authHdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/admin/order-settings/duplicate-warning`, { headers: authHdrs() })
      .then((r) => setDays(r.data?.days ?? 7))
      .catch(() => setDays(7));
  }, []);

  const setVal = async (v) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/order-settings/duplicate-warning`,
        { days: Number(v) }, { headers: authHdrs() });
      setDays(Number(v));
      toast.success(`Duplicate warning window set to ${v} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="duplicate-warning-card">
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-lg text-slate-900">Duplicate Order Warning Period</h3>
          <p className="text-sm text-slate-500 mt-1">
            When a franchise re-orders a SKU that is already in pending procurement, we show
            them a warning popup (they can still proceed). This setting controls how far back
            we look for the duplicate.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[7, 15, 30].map((opt) => (
            <button
              key={opt} disabled={saving || days === opt}
              onClick={() => setVal(opt)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                days === opt
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
              }`}
              data-testid={`dup-warning-period-${opt}`}
            >
              {opt} Days
            </button>
          ))}
          {days != null && (
            <span className="text-xs text-slate-500 ml-2">
              Currently: <span className="font-semibold text-slate-700">{days} days</span>
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default OrderControlPanel;

// ────────────────────────────────────────────────────────────────────
// Cart Retention Period — controls how long an inactive Store-Portal
// shopping cart is preserved before it auto-clears. -1 means never.
// ────────────────────────────────────────────────────────────────────
function CartRetentionCard() {
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);
  const authHdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/admin/order-settings/cart-retention`, { headers: authHdrs() })
      .then((r) => setDays(r.data?.days ?? 30))
      .catch(() => setDays(30));
  }, []);

  const setVal = async (v) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/order-settings/cart-retention`,
        { days: Number(v) }, { headers: authHdrs() });
      setDays(Number(v));
      toast.success(v === -1 ? 'Cart will never auto-expire' : `Cart retention set to ${v} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const options = [
    { v: 7,  label: '7 Days' },
    { v: 15, label: '15 Days' },
    { v: 30, label: '30 Days' },
    { v: -1, label: 'Never Expire' },
  ];

  return (
    <Card className="border-slate-200" data-testid="cart-retention-card">
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-lg text-slate-900">Cart Retention Period</h3>
          <p className="text-sm text-slate-500 mt-1">
            How long a Store-Portal user&apos;s shopping cart is preserved when they&apos;re away.
            The cart survives refresh, logout, and device-switches by default — this only
            controls auto-clearing of <em>truly inactive</em> carts.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {options.map((opt) => (
            <button
              key={opt.v} disabled={saving || days === opt.v}
              onClick={() => setVal(opt.v)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                days === opt.v
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
              }`}
              data-testid={`cart-retention-period-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
          {days != null && (
            <span className="text-xs text-slate-500 ml-2">
              Currently: <span className="font-semibold text-slate-700">
                {days === -1 ? 'Never expire' : `${days} days`}
              </span>
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Invoice → Order Backfill — one-time historical sync. Scans every
// finalised invoice that hasn't been synced yet and flips matching
// franchise-order lines to invoiced / partially_invoiced.
// Inventory is NEVER touched here — that was already deducted at the
// time the invoice was finalised.
// ────────────────────────────────────────────────────────────────────
function InvoiceOrderSyncBackfillCard() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [limit, setLimit] = useState(50);
  const [force, setForce] = useState(false);
  const authHdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const run = async () => {
    if (!window.confirm(
      `Run invoice → order backfill ${force ? '(force re-sync, all finalised invoices)' : `(first ${limit} unsynced invoices)`}? ` +
      `This will NOT touch inventory — only order line statuses, invoice references, and pending qty are updated.`,
    )) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/invoice-order-sync/backfill`,
        { limit: Number(limit), force }, { headers: authHdrs() });
      setReport(r.data);
      toast.success(`Backfill done — ${r.data.total_lines_updated} order lines updated across ${r.data.invoices_processed} invoices`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally { setRunning(false); }
  };

  return (
    <Card className="border-indigo-200" data-testid="invoice-sync-backfill-card">
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-lg text-slate-900">Invoice → Order Backfill (One-Time)</h3>
          <p className="text-sm text-slate-500 mt-1">
            Historical invoices created BEFORE this sync feature shipped left their franchise-order
            lines stuck in <span className="font-mono text-xs">pending_procurement</span> or
            <span className="font-mono text-xs"> ready_for_pick</span>. Run this once to catch them up.
            Going forward, every new invoice auto-syncs at finalize time.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <span>Batch size</span>
            <input type="number" min="1" max="5000" value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="border rounded px-2 py-1 w-24 text-sm"
              data-testid="invoice-sync-limit" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={force}
              onChange={(e) => setForce(e.target.checked)}
              data-testid="invoice-sync-force" />
            Force re-sync (include already-synced invoices)
          </label>
          <button
            onClick={run} disabled={running}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              running ? 'bg-slate-200 text-slate-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
            data-testid="invoice-sync-run-btn"
          >
            {running ? 'Running…' : 'Run Backfill'}
          </button>
        </div>
        {report && (
          <div className="text-xs bg-slate-50 border rounded p-3 max-h-64 overflow-y-auto"
            data-testid="invoice-sync-report">
            <p className="font-semibold">
              Processed {report.invoices_processed} invoices · Updated {report.total_lines_updated} order lines
            </p>
            <table className="w-full mt-2">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1">Invoice</th>
                  <th className="text-right py-1">Lines updated</th>
                  <th className="text-right py-1">Orders touched</th>
                  <th className="text-right py-1">Unmatched SKUs</th>
                </tr>
              </thead>
              <tbody>
                {(report.details || []).map((d) => (
                  <tr key={d.invoice_id} className="border-t">
                    <td className="py-1 font-mono">{d.invoice_number}</td>
                    <td className="py-1 text-right">{d.error ? '—' : d.lines_updated}</td>
                    <td className="py-1 text-right">{d.error ? '—' : d.orders_updated}</td>
                    <td className="py-1 text-right">{d.error ? <span className="text-rose-600">err</span> : d.unmatched_skus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

